import { useState, useCallback, useEffect } from "react";
import PromocionEditarModal from "../../Factura/Promociones/PromocionEditarModal";
import { ListadoPromocion, EliminarPromocion, actualizarPromocion } from "../../../Service/Service.promocion";
import usePermissions from "../../../hook/usePermissions";
import useListaLocal from "../../../hook/useListaLocal";
import DataTable from "../../UI/DataTable/DataTable";
import { Celda } from "../../UI/DataTable/piezas";
import MenuAcciones from "../../UI/MenuAcciones";
import Modal from "../../UI/Modal/Modal";
import { useToast } from "../../UI/Aviso";
import { CampoTexto } from "../../UI/Campo";
import { IconoEditar, IconoEliminar, IconoBuscar } from "../../UI/Iconos";

function PromocionListado({ data }) {
    // RBAC-CUTOVER-07: Editar depende de promociones.edit, Eliminar de
    // promociones.delete (backend ya los exige en promocion.routes.ts).
    const { hasPermission } = usePermissions();
    // SEGUROS-PLANES FASE 2: `seguroId` OPCIONAL. Sin el, el listado es el de
    // siempre (todas las promociones de la clinica). Con el, el SERVIDOR filtra
    // por aseguradora (P1) -- no se descarga el resto para tirarlo en el
    // navegador.
    const { reloadPromociones, seguroId = null } = data;
    const [loading, setLoading] = useState(false);
    const [listarPromociones, setListarPromociones] = useState([]);
    const [error, setError] = useState(null);
    const [editingPromocion, setEditingPromocion] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [porEliminar, setPorEliminar] = useState(null);
    const [eliminando, setEliminando] = useState(false);
    const { mostrar } = useToast();
    const lista = useListaLocal(listarPromociones, { campos: ["nombre", "Seguros.nombre"] });

    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        try {
            const promociones = await ListadoPromocion(seguroId ? { seguro_id: seguroId } : {});
            const listado = promociones.data.map((item) => ({
                ...item,
                key: item.id_promocion,
            }));
            setListarPromociones(listado);
            setError(null);
        } catch (error) {
            // Antes esto era un aviso que se desvanecía sobre una tabla vacía,
            // que es lo mismo que ver "no hay promociones".
            setListarPromociones([]);
            setError("Error al cargar promociones");
        } finally {
            setLoading(false);
        }
    }, [seguroId]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData, reloadPromociones]);

    const handleEdit = (record) => {
        setEditingPromocion(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        setEliminando(true);
        try {
            const response = await EliminarPromocion(id);
            if (response.success) {
                mostrar({ tipo: "exito", texto: response.message || "Promoción desactivada correctamente" });
                fetchInitialData(); // <- Recarga la tabla
            } else {
                throw new Error(response.message);
            }
        } catch (error) {
            mostrar({ tipo: "error", texto: error.message || "Error al desactivar la promoción" });
        } finally {
            setEliminando(false);
            setPorEliminar(null);
        }
    };


    const handleSave = async (payload) => {
        try {
            if (editingPromocion) {
                await actualizarPromocion(editingPromocion.id_promocion, payload);
                mostrar({ tipo: "exito", texto: "Promoción actualizada correctamente" });
            }
            fetchInitialData();
            setModalVisible(false);
        } catch (error) {
            mostrar({ tipo: "error", texto: "Error al guardar los cambios" });
        }
    };

    const columnas = [
        { key: "nombre", titulo: "Promoción", render: (p) => <Celda valor={p.nombre} /> },
        {
            key: "seguro",
            titulo: "Seguro/Entidad",
            ancho: 240,
            render: (p) => <Celda valor={p.Seguros?.nombre} />,
        },
        {
            key: "acciones",
            titulo: "",
            ancho: 60,
            alinear: "centro",
            render: (p) => {
                const acciones = [];
                if (hasPermission("promociones.edit")) {
                    acciones.push({ label: "Editar", icono: IconoEditar, onClick: () => handleEdit(p) });
                }
                if (hasPermission("promociones.delete")) {
                    acciones.push({
                        label: "Eliminar",
                        icono: IconoEliminar,
                        peligro: true,
                        onClick: () => setPorEliminar(p),
                    });
                }
                return acciones.length > 0 ? <MenuAcciones acciones={acciones} titulo={`Acciones de ${p.nombre}`} /> : null;
            },
        },
    ];

    return (
        <>
            <div className="mb-3" style={{ maxWidth: 380 }}>
              <CampoTexto
                etiqueta="Buscar promoción"
                icono={IconoBuscar}
                placeholder="Nombre o seguro"
                value={lista.texto}
                onChange={(e) => lista.setTexto(e.target.value)}
                data-testid="promocion-buscar"
              />
            </div>

            <DataTable
                columnas={columnas}
                filas={lista.visibles}
                filaKey="id_promocion"
                cargando={loading}
                error={error}
                onReintentar={fetchInitialData}
                etiquetaTabla="Promociones"
                hayFiltrosAplicados={lista.hayBusqueda}
                onLimpiarFiltros={lista.limpiar}
                textoVacioSinFiltros="Todavía no hay promociones."
                paginacion={lista.paginacion}
                expansion={{
                    expandible: (p) => !!p.detalles,
                    render: (p) =>
                        p.detalles?.length > 0 ? (
                            <ol className="cfg-detalle">
                                {p.detalles.map((detalle) => (
                                    <li className="cfg-detalle__fila" key={detalle.id_tipo_examen}>
                                        <div>
                                            <div className="cfg-detalle__titulo">
                                                {detalle.ExamenTipo?.codigo}: {detalle.ExamenTipo?.descripcion}
                                            </div>
                                            <span className="cfg-detalle__dato">
                                                P. Normal: ${detalle.tarifario?.precio?.toFixed(2)}
                                            </span>
                                        </div>
                                        <span className="cfg-detalle__destacado">
                                            P. Promoción: ${detalle.precio_promocion?.toFixed(2)}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <p className="mb-0">No hay exámenes en esta promoción</p>
                        ),
                }}
            />

            {/* La confirmación era un `Popconfirm` de dos palabras ("¿Eliminar
                esta promoción?" / Sí / No) que no decía CUÁL ni qué implica. */}
            <Modal
                abierto={Boolean(porEliminar)}
                titulo="Eliminar promoción"
                destructivo
                onCerrar={() => setPorEliminar(null)}
                secundario={{ label: "Cancelar" }}
                primario={{
                    label: "Eliminar",
                    cargando: eliminando,
                    onClick: () => handleDelete(porEliminar.id_promocion),
                }}
            >
                <p>
                    Vas a eliminar <b>{porEliminar?.nombre}</b>
                    {porEliminar?.Seguros?.nombre ? ` (${porEliminar.Seguros.nombre})` : ""}.
                </p>
            </Modal>

            <PromocionEditarModal
                data={editingPromocion}
                visible={modalVisible}
                handleClose={() => setModalVisible(false)}
                onSave={handleSave}
            />
        </>
    );
}

export default PromocionListado;
